import { fromJS, List, Map } from 'immutable';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';
import { select } from 'redux-saga/effects';

import * as PlaybackConstants from './constants';
import * as D from './deps';

export function* getCurrentTime({
  id,
  next,
  starting,
  seekTime = null,
  noSeekOnStart,
  previous,
  forceRestart,
} = {}) {
  let currentTime;
  if (seekTime || seekTime === 0) {
    currentTime = seekTime;
  } else if (next || previous || forceRestart) {
    currentTime = 0;
  } else if (starting) {
    if (noSeekOnStart) {
      currentTime = 0;
    } else {
      currentTime =
        (yield select(D.getInDomain('playback.utils.getSeekToTimeInSeconds'), id)) || currentTime;
    }
  } else {
    // Prefer the time from the audio player, otherwise get the time from the playlist -- should be the same though
    currentTime =
      (yield select(D.getInDomain('playback.utils.getCurrentTrackTime'))) ||
      (yield select(D.getInDomain('playback.utils.getCurrentPlaylistTrackTime'))) ||
      currentTime;
  }
  return isNaN(currentTime) ? 0 : currentTime;
}

export function getTrackArt(track, state, size = 500) {
  if (D.Utils.VoiceTrack.isStationVoiceTrack(track)) {
    const annotation = D.getInDomain('catalog.utils.getAnnotations')(state, track.get('pandoraId'));
    return D.Utils.Art.getArt(annotation, size, state);
  }

  return D.Utils.Art.getArt(track, size, state);
}

/**
 *
 * @param {object} state
 * @param {string} playlistId
 * @returns {*}
 */
function getPlaylistTrackModelType(state, playlistId) {
  if (!state.playlist) return null;
  return state.playlist.getIn(
    [
      PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      PlaybackConstants.PlaylistKeys.PLAYLIST_TRACK_MODEL_TYPE,
    ],
    'PlaylistTrack',
  );
}

export function getCurrentPlaylistTrackModelType(state) {
  return getPlaylistTrackModelType(
    state,
    D.getInDomain('playback.utils.getCurrentPlaylist', null)(state),
  );
}

export function isPlaylistModelTypeAPSItem(state, playlistId) {
  const id = playlistId || D.getInDomain('playback.utils.getCurrentPlaylist')(state);
  return getPlaylistTrackModelType(state, id) === 'APSItem';
}

/**
 * Return the token of the last track played on this playlist
 *
 * @param  {object} state - the redux state
 * @param  {string} playlistId - the id of the playlist to query
 * @returns {string} the token of the last track played on this playlist (null if not found)
 */
export function getPlaylistCurrentToken(state, playlistId) {
  if (!state.playlist) return null;
  return state.playlist.getIn(
    [
      PlaybackConstants.PlaylistKeys.PLAYLISTS,
      playlistId,
      PlaybackConstants.PlaylistKeys.CURRENT_TOKEN,
    ],
    null,
  );
}

export function getPlaylistCurrentTrack(state, playlistId) {
  if (!playlistId) return null;
  const type = getPlaylistTrackModelType(state, playlistId);
  const token = getPlaylistCurrentToken(state, playlistId);
  return D.Utils.Model.resolveModelFromKey(state, token, type);
}

export function getCurrentTrackFromActivePlaylist(state) {
  const type = getCurrentPlaylistTrackModelType(state);
  const token = getCurrentTrackTokenFromActivePlaylist(state);
  return D.Utils.Model.resolveModelFromKey(state, token, type);
}

export function getCurrentTrackTokenFromActivePlaylist(state) {
  return getPlaylistCurrentToken(
    state,
    D.getInDomain('playback.utils.getCurrentPlaylist', null)(state),
  );
}

export function getCurrentHistoryFromActivePlaylist(state) {
  if (getCurrentPlaylistTrackModelType(state) === 'APSItem') {
    return D.getInDomain('playback.selectors.getSessionList', List())(state);
  }
  return D.getInDomain('playback.utils.getStationHistory', List())(state);
}

export function getTrackInSessionHistory(state) {
  const type = getCurrentPlaylistTrackModelType(state);
  const sessionHistory = getCurrentHistoryFromActivePlaylist(state);
  const sessionHistoryIndex = state.nowPlaying.get(
    D.Constants.NowPlaying.NowPlayingKeys.SESSION_HISTORY_INDEX_KEY,
  );
  const token = sessionHistory.get(sessionHistoryIndex);
  return D.Utils.Model.resolveModelFromKey(state, token, type);
}

export function getTrackTokenFromActivePlaylistByPlaylistType(state, track) {
  const type = getCurrentPlaylistTrackModelType(state);
  return type === 'APSItem' || D.Utils.Listener.isAPSPandoraStationEnabled(state)
    ? D.Utils.Model.getFromModel(track, type, 'sessionTrackToken', state)
    : D.Utils.Model.getFromModel(track, type, 'trackToken', state);
}

// Util for getting an attribute from a certain model type, such as PlaylistTrack
// Playlists keep track of the the type of tracks that they contain
export function getAttributeForTrackFromActivePlaylist(state, track, attr) {
  const type = getCurrentPlaylistTrackModelType(state);
  return D.Utils.Model.getFromModel(track, type, attr, state);
}

export function getAttributeForActiveTrack(state, attr) {
  const type = getCurrentPlaylistTrackModelType(state);
  const track = getCurrentTrackFromActivePlaylist(state);
  return D.Utils.Model.getFromModel(track, type, attr, state);
}

export function isTrackPlayingCurrently(state, pandoraId) {
  const currentTrack = getCurrentTrackFromActivePlaylist(state);
  return !!currentTrack && currentTrack.get('pandoraId') === pandoraId;
}

export function isSourcePlayingCurrently(state, id) {
  const currentPlaylist = D.getInDomain('playback.utils.getCurrentPlaylist', null)(state);
  return !!currentPlaylist && currentPlaylist === id;
}

/**
 *
 * @param {object} state
 * @returns {*|boolean}
 */
// TODO: don't need to use getInDomain
export function isAudioPlaying(state) {
  // We usually will just take what's going on in the audio player
  // but sometimes, usually between tracks, we want to consider that as 'playing'
  return (
    D.getInDomain('playback.utils.isAudioPlayerPlaying', false)(state) ||
    (!D.getInDomain('playback.utils.isExplicitlyPaused', false)(state) &&
      D.getInDomain('playback.utils.hasCurrentPlaylistPlayedRecently', false)(state))
  );
}

export function getPandoraIdAndSourceTypeFromUrlParams(
  state,
  {
    parentType,
    pseudoType,
    artistPandoraIdWithoutPrefix,
    albumPandoraIdWithoutPrefix,
    trackPandoraId,
    stationThumbsPandoraId,
    playlistPandoraId,
    podcastPandoraId,
    episodePandoraId,
  },
) {
  let sourceType = D.Utils.SourceType.getPseudoSourceTypeFromUrlParams(parentType, pseudoType);
  let pandoraId;

  if (artistPandoraIdWithoutPrefix) {
    const path = D.Utils.Route.getCurrentPathnameFromState(state);
    const paths = (path && path.split('/')) || [];
    const artistPandoraIdWithPrefix = paths[paths.length - 1]; // need to pull from path because it could be AR or CO prefixed
    if (sourceType === D.Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS) {
      pandoraId = D.Utils.SourceType.getArtistAllSongsId(artistPandoraIdWithPrefix);
    } else if (sourceType === D.Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS) {
      pandoraId = D.Utils.SourceType.getArtistTopSongsId(artistPandoraIdWithPrefix);
    }
  } else if (albumPandoraIdWithoutPrefix) {
    const albumId = `${D.Constants.SourceTypes.PandoraTypes.ALBUM}${albumPandoraIdWithoutPrefix}`;
    const collectedAlbumTracksId = D.Utils.SourceType.getCollectedAlbumTracksId(albumId);
    if (isSourcePlayingCurrently(state, collectedAlbumTracksId)) {
      pandoraId = collectedAlbumTracksId;
      sourceType = D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS;
    } else {
      pandoraId = albumId;
      sourceType = D.Constants.SourceTypes.SourceTypes.ALBUM;
    }
  } else if (trackPandoraId) {
    pandoraId = trackPandoraId;
    sourceType = D.Constants.SourceTypes.SourceTypes.SONG;
  } else if (stationThumbsPandoraId) {
    pandoraId = stationThumbsPandoraId;
    sourceType = D.Constants.SourceTypes.SourceTypes.STATION_THUMB_UP;
  } else if (playlistPandoraId) {
    pandoraId = playlistPandoraId;
    sourceType = D.Constants.SourceTypes.SourceTypes.PLAYLIST;
  } else if (pseudoType === D.Constants.Catalog.SourceTypeUrlParams.ALL_SONGS) {
    const listenerId = D.Utils.Listener.getCurrentListenerIdFromState(state);
    pandoraId = D.Utils.SourceType.getCollectedTrackPseudoId(listenerId);
  } else if (podcastPandoraId) {
    pandoraId = podcastPandoraId;
    sourceType = D.Constants.SourceTypes.SourceTypes.PODCAST;
  } else if (episodePandoraId) {
    pandoraId = episodePandoraId;
    sourceType = D.Constants.SourceTypes.SourceTypes.EPISODE;
  }

  if (!D.Utils.Music.isOnDemandIdLike(pandoraId)) {
    // We might parse an id from the URL, but it might not be something that we can play
    // if it's not a pandoraId return null
    pandoraId = null;
  }

  return { pandoraId, sourceType };
}

// We want getEnabledPlaybackControls to return identical immutable objects when
// the set of enabled controls is the same, this will prevent unnecessary rerenders
// that would otherwise trigger because `List() !== List()`
// (see https://medium.com/@esamatti/react-js-pure-render-performance-anti-pattern-fb88c101332f#.lsj5y44w3)
export const controlsMemoizationHelper = memoize(object => {
  let returnVal = object;
  if (isString(object)) {
    returnVal = object.split(',');
  }
  return fromJS(returnVal) || List();
});

/**
 * Return which playback controls are enabled. Play and Skip are enabled by default.
 * NOTE: The capabilities are accumulated using strings so that we can memoize more
 * efficiently (using the string capabilities as the keys)
 *
 * @param {object} state - the redux store state
 * @param {object} track - the track to determine which controls are enabled for
 * @returns {List} list of enabled playback controls
 */
export function getEnabledPlaybackControls(state, track) {
  if (!track || D.Utils.Overlay.shouldOverlaysDisableTuner(state)) {
    return controlsMemoizationHelper();
  }

  let capabilities = [
    D.Constants.Playback.PLAYBACK_CONTROLS.PLAY,
    D.Constants.Playback.PLAYBACK_CONTROLS.VOLUME,
  ];

  if (isPlaylistModelTypeAPSItem(state)) {
    capabilities = capabilities.concat(D.getInDomain('playback.selectors.getInteractions')(state));
  } else {
    if (D.getInDomain('playback.utils.allowFeedbackForTrack')(state, track)) {
      capabilities.push(D.Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
    }

    const isAudioAd = D.getInDomain('ads.utils.isAudioAd', false)(state, track);

    if (
      track.get('allowSkip') &&
      (!isAudioAd || D.getInDomain('playback.utils.getCurrentTrackTime')(state) > 0)
    ) {
      capabilities.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP);
    }

    if (
      D.Utils.Playlist.doesTrackHaveReplayRights(track) ||
      D.Utils.DebugSettings.getTestCoachmarksType()
    ) {
      capabilities.push(D.Constants.Playback.PLAYBACK_CONTROLS.REPLAY);
    }
  }

  // capabilities should be a string like such at this point: 'play,pause'
  return controlsMemoizationHelper(capabilities.join(','));
}

export function getPlaybackControlsForSourceType(sourceType, personalizedPlaylist) {
  const controls = [
    D.Constants.Playback.PLAYBACK_CONTROLS.PLAY,
    D.Constants.Playback.PLAYBACK_CONTROLS.VOLUME,
  ];

  if (D.Constants.SourceTypes.PodcastSourceTypes.includes(sourceType)) {
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP_15_BACK);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP_15_FORWARD);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SCRUB);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
  } else if (
    sourceType &&
    ![
      D.Constants.SourceTypes.SourceTypes.STATION,
      D.Constants.SourceTypes.SourceTypes.AUTOPLAY,
    ].includes(sourceType)
  ) {
    // on demand
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.REPEAT);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP_BACK);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP_FORWARD);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SHUFFLE);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SCRUB);
    if (personalizedPlaylist) {
      controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
    }
  } else {
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.REPLAY);
    controls.push(D.Constants.Playback.PLAYBACK_CONTROLS.SKIP);
  }

  return controlsMemoizationHelper(controls.join(','));
}

/**
 * Check if replay control should be shown on a track. Depends on
 * track-specific rights and if the feature is available to the
 * current listener.
 *
 * @param  {object} state - redux store
 * @param  {object} track - the track to check replay feature on
 * @returns {boolean} - whether or not to show replay control
 */
export function shouldShowReplayControl(state, track) {
  let inRewardsSet = true;

  const isAudioAd = D.getInDomain('ads.utils.isAudioAd', false)(state, track);

  // show replay button to anon listeners or if it is an audio ad or artist message
  if (
    D.Utils.Listener.isAnonymousListener(state) ||
    isAudioAd ||
    D.Utils.Playlist.isArtistMessage(track) ||
    D.Utils.VoiceTrack.isStationVoiceTrack(track)
  ) {
    return true;
  }

  if (track) {
    const currentUserReplayableQueue = D.getInDomain(
      'rewards.utils.getCurrentUserReplayableQueue',
      null,
    )(state);
    if (currentUserReplayableQueue) {
      inRewardsSet = currentUserReplayableQueue.has(D.Utils.Playlist.getTrackMusicId(track));
    }
  }

  return inRewardsSet && D.Utils.Playlist.getRightsShowReplayButton(track);
}

/**
 * Returns the relevant track for the getAdList call
 *
 * @param {object} state - the redux store state
 * @param {string} playlistId - the id of the playlist to query
 * @returns {Map|null} The track that should be used to call getAdList
 */
export function getCurrentTrackForAdList(state, playlistId) {
  // See if we can get the track from now playing
  let track = getCurrentTrackFromActivePlaylist(state);

  // If the track can't be recovered see if it can be restored from the playlist
  if (!track) {
    const trackToken = getPlaylistCurrentToken(state, playlistId);
    track = D.Utils.Entity.getTrackById(state, trackToken);

    // If we have the token and not the track we are probably reloading during an ad
    // Return the last playlist track we played
    if (trackToken && !track) {
      track = D.getInDomain('playback.utils.getCurrentAudioMapFromPlaylist', null)(
        state,
        playlistId,
      );
    }
  }

  return track;
}

// This function gets a source list and returns the index that a track
// would be in its original track ordering. So in the case of shuffle we
// might be playing the third shuffled track, which is actually # 7 in an album
// so this function is to get 7 for as the original index
export function getTrackIndexInOriginalSource(state, { trackId, sourceId, sortOrder }) {
  const tracks = D.getInDomain('catalog.utils.getTrackGetterBySourceType')(
    D.Utils.SourceType.getSourceTypeFromPandoraId(sourceId),
  )(state, sourceId, sortOrder);
  if (!tracks) return -1;
  return tracks.indexOf(trackId);
}

export function getCurrentTrackCountOfOriginalSource(state) {
  const id = D.getInDomain('catalog.utils.mapPseudoSourceToParent')(
    state,
    D.getInDomain('playback.utils.getCurrentPlaylist')(state),
  );
  const sourceType = D.getInDomain('playback.utils.getCurrentPlaylistType')(state);
  switch (sourceType) {
    case D.Constants.SourceTypes.SourceTypes.SONG:
      return 1;
    case D.Constants.SourceTypes.SourceTypes.ALBUM:
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS:
      return D.getInDomain('catalog.utils.getAnnotations')(state, id).get('trackCount');
    case D.Constants.SourceTypes.SourceTypes.PLAYLIST:
      return D.getInDomain('catalog.utils.getAnnotations')(state, id).get('totalTracks');
    case D.Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS:
      return D.getInDomain('catalog.utils.getArtistTopSongs')(state, id).size;
    case D.Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS:
      return D.getInDomain('catalog.utils.getAllArtistTracksFromState')(state, id).size;
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS:
      return D.getInDomain('collection.utils.getFilteredCollectionTotalCount', 0)(
        state,
        D.Constants.FilterNav.FilterNavTypes.SONGS,
      );
    case D.Constants.SourceTypes.SourceTypes.STATION_THUMB_UP:
      return D.getInDomain('playback.utils.getStationByStationId')(state, id).get(
        'positiveFeedbackCount',
      );
    default:
      return 0;
  }
}

export function resolveAudioTrackForPlaylist(state, token, playlistId) {
  const type = getPlaylistTrackModelType(state, playlistId);
  if (type === 'APSItem') {
    return D.getInDomain('playback.selectors.getCurrentItemAudioTrack')(state);
  }
  return D.Utils.Model.resolveModelFromKey(state, token, type);
}

// Returns a track map from a token, first trying to resolve to a PlaylistTrack
// model, then trying an APSItem (details from APS and catalog)
export function getDetailedTrack(state, trackToken) {
  const isAPSEnabled = D.Utils.Listener.isAPSPandoraStationEnabled(state);
  if (isAPSEnabled) {
    return D.Utils.Model.getFirstModel(['APSItem'], state, trackToken);
  }
  return D.Utils.Model.getFirstModel(['PlaylistTrack', 'APSItem'], state, trackToken);
}

/**
 * @param  {object} state - the redux state
 * @returns {boolean} - are we currently updating feedback?
 */

export function isUpdatingFeedback(state) {
  return state.nowPlaying.get(D.Constants.NowPlaying.NowPlayingKeys.IS_UPDATING_FEEDBACK_KEY);
}

/**
 * @param  {object} state - the redux state
 * @returns {boolean} - are we currently marking track as tired
 */
export function isMarkingTrackAsTired(state) {
  return state.nowPlaying.get(D.Constants.NowPlaying.NowPlayingKeys.IS_MARKING_TRACK_AS_TIRED);
}

export function getNumberOfTrackRebuffers(state) {
  return state.rebuffer.get(D.Constants.Rebuffer.RebufferKeys.TRACK_CAN_PLAY_TRACK_COUNT);
}

export function getRecommendedInitialSourceFromRouteData(state, routeData) {
  const { seoToken, trackToken } = routeData;
  let { stationId } = routeData;
  const currentPathname = D.Utils.Route.getCurrentPathnameFromState(state);
  const { sourceType, pandoraId } = getPandoraIdAndSourceTypeFromUrlParams(state, routeData);

  // first see if route passed in an id and a trackToken, unless the route is Station details page
  let sourceId =
    D.Utils.Route.isStationDetailsPath(currentPathname) ||
    D.Utils.Route.isStationThumbsUpPath(currentPathname)
      ? null
      : stationId;
  let type = D.Constants.SourceTypes.SourceTypes.USER_STATION;
  let sortOrder;

  if (pandoraId) {
    const isOnDemandNowPlaying = D.Utils.Route.isCurrentRouteOnDemandNowPlaying(state);
    const currentItemSourceId = D.getInDomain(
      'playback.selectors.getCurrentItemSourceId',
      null,
    )(state);
    const originalSourceId = D.getInDomain('playback.selectors.getSourcePandoraId', null)(state);
    const isAutoplaying =
      D.Utils.SourceType.getPandoraTypeFromPandoraId(currentItemSourceId) ===
      D.Constants.SourceTypes.PandoraTypes.AUTOPLAY;
    const shouldAutoplay =
      D.Utils.Listener.hasPremiumFeatures(state) &&
      isOnDemandNowPlaying &&
      isAutoplaying &&
      pandoraId === originalSourceId;
    type = shouldAutoplay ? D.Constants.SourceTypes.SourceTypes.AUTOPLAY : sourceType;
    sourceId = shouldAutoplay ? currentItemSourceId : pandoraId;
  }

  // if no stationId is provided in the URL, see if route passed in seoToken
  if (!sourceId) {
    if (seoToken) {
      sourceId = D.Constants.Station.StationConstants.SEO_TOKEN + seoToken;
    }
  }

  // if the URL has no stationID/seoToken, get current station
  if (!sourceId) {
    sourceId = D.getInDomain('playback.utils.getCurrentPlaylist')(state);
    type = D.getInDomain('playback.utils.getCurrentPlaylistType')(state);
  }

  // fall back to last played station
  if (!sourceId) {
    const lastPlayedStationId = D.getInDomain('playback.utils.getLastPlayedStationId')(state);
    if (lastPlayedStationId) {
      sourceId = stationId = lastPlayedStationId;
      type = D.Constants.SourceTypes.SourceTypes.USER_STATION;
    }
  }

  if (sourceId) {
    const currentSource = D.getInDomain('playback.selectors.getSource', Map())(state);
    sortOrder = sourceId === currentSource.get('pandoraId') ? currentSource.get('sortOrder') : null;
  }

  return { sortOrder, sourceId, stationId, trackToken, type };
}

export function isItemPlayable(pandoraType) {
  return (
    pandoraType === D.Constants.SourceTypes.PandoraTypes.ALBUM ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.STATION ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.GENRE ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.PLAYLIST ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.STATION ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.ARTIST_ALL_SONGS ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.PODCAST ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.ARTIST_TOP_SONGS ||
    pandoraType === D.Constants.SourceTypes.PandoraTypes.SONG
  );
}

export function isAutoStartEnabled(state) {
  return state.playback.get(PlaybackConstants.PlaybackKeys.SHOULD_AUTO_START);
}

export function isSourceStarting(state) {
  return state.playback.get(PlaybackConstants.PlaybackKeys.SOURCE_STARTING);
}

export function hasPlaybackStartedInSession(state) {
  return state.playback.get(PlaybackConstants.PlaybackKeys.HAS_PLAYBACK_STARTED_IN_SESSION);
}
